import { FileText, Receipt } from 'lucide-react'
import { Link } from '@tanstack/react-router'

import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PipelinePopoverPanel, usePipelineHoverPopover } from '@/components/pipeline/pipeline-popover-panel'
import { jobDetailWithTipText } from '@/components/pipeline/job-detail-view'
import { useJobInvoiceBreakdown } from '@/stores/pipeline.store'
import { formatCurrency, formatCurrencyWhole } from '@/lib/currency-utils'
import { formatShortDate } from '@/lib/date-utils'
import { getInvoiceStatusColor, getInvoiceStatusLabel, getQuoteStatusColor, getQuoteStatusLabel } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import type { JobLite } from '@/gen/cortado/v1/jobs_pb'
import {
  pipelinePaymentSegmentDisplay,
  pipelinePaymentSegmentFillClass,
  pipelinePaymentsDocKind,
  pipelineQuotedAmountClass,
  pipelineQuotedAmountText,
  pipelineQuotedPaidPopoverTitle,
  pipelineQuotedSubLabel,
  pipelineQuotedSubLabelClass,
  PIPELINE_CELL_BAR_ROW_CLASS,
  PIPELINE_CELL_INFO_CLASS,
  PIPELINE_CELL_STACK_CLASS,
  PIPELINE_CELL_TITLE_CLASS,
} from '@/components/pipeline/pipeline-job-view'

/** Each invoice and quote row opens that record's page inside the pipeline. */
const POPOVER_ROW_CLASS =
  'grid grid-cols-[1fr_auto_auto] items-center gap-2.5 border-b border-border px-3 py-2 last:border-b-0 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none'

interface PipelineInvoicePopoverCellProps {
  job: JobLite
}

/**
 * Quoted/paid cell that lazily fetches the invoice + quote breakdown
 * (GetJob → GetInvoiceLitesByIds / GetQuoteLitesByIds) only once its popover
 * is opened — this keeps the list render itself to a single ListJobs call.
 * Opens on hover (with a short open/close delay — see
 * `usePipelineHoverPopover`) or click/focus so it stays keyboard accessible.
 *
 * Invoices are the primary list once any exist; quotes render below them in
 * a muted, secondary section. With no invoices yet, quotes render as the
 * primary (non-muted) list instead.
 */
export function PipelineInvoicePopoverCell({ job }: PipelineInvoicePopoverCellProps) {
  const { open, onOpenChange, triggerHandlers, contentHandlers } = usePipelineHoverPopover()
  const { invoices, quotes, isLoading, isError, permissionDenied } = useJobInvoiceBreakdown(job.id, open)
  const hasInvoices = invoices.length > 0
  const docKind = pipelinePaymentsDocKind(job)
  const DocIcon = docKind === 'quote' ? FileText : docKind === 'invoice' ? Receipt : null

  const totalDueCents = invoices.reduce((sum, invoice) => sum + invoice.amountDueCents, 0n)
  const totalPaidCents = invoices.reduce((sum, invoice) => sum + (invoice.totalCents - invoice.amountDueCents), 0n)
  const footerStatus =
    invoices.length === 0
      ? ''
      : totalDueCents <= 0n
        ? 'Paid in full'
        : totalPaidCents > 0n
          ? 'Partially paid'
          : 'Unpaid'
  const footerText =
    footerStatus && totalDueCents > 0n
      ? `${footerStatus} · $${formatCurrencyWhole(Number(totalDueCents))} outstanding`
      : footerStatus

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="block w-full min-w-0 rounded-md px-1 py-0.5 text-right hover:bg-accent"
          onClick={(event) => event.stopPropagation()}
          {...triggerHandlers}
        >
          <div className={PIPELINE_CELL_STACK_CLASS}>
            <div className="flex items-center justify-end gap-1">
              {DocIcon && <DocIcon size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />}
              <span className={cn(PIPELINE_CELL_TITLE_CLASS, 'font-financial', pipelineQuotedAmountClass(job))}>
                {pipelineQuotedAmountText(job)}
              </span>
              {docKind !== 'none' && <span className="sr-only">{docKind === 'quote' ? 'Quote' : 'Invoice'}</span>}
            </div>
            <div className={PIPELINE_CELL_BAR_ROW_CLASS}>
              {job.paymentSegments.map((segment, index) => {
                const display = pipelinePaymentSegmentDisplay(job, segment)
                return (
                  <span
                    key={index}
                    title={display.label}
                    aria-label={display.label}
                    className={cn('flex-1 rounded-sm', pipelinePaymentSegmentFillClass(display.tone, docKind))}
                  />
                )
              })}
            </div>
            <span className={cn(PIPELINE_CELL_INFO_CLASS, pipelineQuotedSubLabelClass(job))}>
              {pipelineQuotedSubLabel(job)}
            </span>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={0}
        className="w-96 p-0 text-left"
        onClick={(event) => event.stopPropagation()}
        {...contentHandlers}
      >
        <div className="text-xs">
          <PipelinePopoverPanel
            title={pipelineQuotedPaidPopoverTitle(job, hasInvoices)}
            count={hasInvoices ? invoices.length : quotes.length}
            isLoading={isLoading}
            loadingText="Loading invoices and quotes…"
            isError={isError}
            permissionDenied={permissionDenied}
            permissionDeniedText="You don't have permission to view invoices."
            errorText="Couldn't load invoices or quotes."
            isEmpty={invoices.length === 0 && quotes.length === 0}
            emptyText="No invoices or quotes yet."
          >
            {invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to="/pipeline/$id/invoices/$invoiceId"
                params={{ id: job.id, invoiceId: invoice.id }}
                className={POPOVER_ROW_CLASS}
                onClick={(event) => event.stopPropagation()}
              >
                <div>
                  <div className="font-medium text-foreground">
                    {invoice.title || `Invoice #${invoice.invoiceNumber}`}
                  </div>
                  <div className="text-muted-foreground">
                    {invoice.quoteId && job.latestQuoteNumber
                      ? `From quote #${job.latestQuoteNumber} · issued ${formatShortDate(invoice.createdAt)}`
                      : `Issued ${formatShortDate(invoice.createdAt)}`}
                  </div>
                </div>
                <span className="font-financial text-foreground">${formatCurrency(Number(invoice.totalCents))}</span>
                <Badge variant={getInvoiceStatusColor(invoice.status)}>{getInvoiceStatusLabel(invoice.status)}</Badge>
              </Link>
            ))}
            {quotes.length > 0 && (
              <div>
                {hasInvoices && (
                  <div className="px-3 pt-2 text-xs uppercase tracking-wide text-muted-foreground/70">Quotes</div>
                )}
                {quotes.map((quote) => {
                  const withTip = jobDetailWithTipText(quote.totalCents, quote.totalWithTipCents)
                  return (
                  <Link
                    key={quote.id}
                    to="/pipeline/$id/quotes/$quoteId"
                    params={{ id: job.id, quoteId: quote.id }}
                    className={cn(POPOVER_ROW_CLASS, hasInvoices && 'text-xs text-muted-foreground opacity-75')}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div>
                      <div className={hasInvoices ? 'font-medium' : 'font-medium text-foreground'}>
                        {quote.title || `Quote #${quote.quoteNumber}`}
                      </div>
                      <div className={hasInvoices ? '' : 'text-muted-foreground'}>
                        {quote.sentAt ? `Sent ${formatShortDate(quote.sentAt)}` : `Created ${formatShortDate(quote.createdAt)}`}
                      </div>
                    </div>
                    <span className={hasInvoices ? 'font-financial text-right' : 'font-financial text-right text-foreground'}>
                      ${formatCurrency(Number(quote.totalCents))}
                      {withTip && <span className="block text-xs text-muted-foreground">{withTip}</span>}
                    </span>
                    <Badge variant={hasInvoices ? 'secondary' : getQuoteStatusColor(quote.status)}>
                      {getQuoteStatusLabel(quote.status)}
                    </Badge>
                  </Link>
                  )
                })}
              </div>
            )}
            <div className="flex items-center justify-between gap-3 px-3 py-2 text-muted-foreground">
              <span className="font-financial">{footerText}</span>
              <Link
                to="/pipeline/$id"
                params={{ id: job.id }}
                className="font-medium text-foreground hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                Open record →
              </Link>
            </div>
          </PipelinePopoverPanel>
        </div>
      </PopoverContent>
    </Popover>
  )
}
